import api from "@/config/api";
import { API_ENDPOINTS } from "@/constants/api-endpoints";
import type { RequestOptions } from "@/shared/types/api.types";
import type { PageResponseDTO } from "@/shared/types/pagination.types";
import type {
  CancelCommercialInvoiceRequestDTO,
  CommercialInvoiceRequestDTO,
  CommercialInvoiceResponseDTO,
  CommercialInvoiceSearchParams,
} from "../types/commercial-invoice.types";

const SEFAZ_ROUND_TRIP_TIMEOUT_MS = 60_000;

export const commercialInvoiceService = {
  search: async (
    params?: CommercialInvoiceSearchParams,
    options?: RequestOptions,
  ): Promise<PageResponseDTO<CommercialInvoiceResponseDTO>> => {
    const response = await api.get<
      PageResponseDTO<CommercialInvoiceResponseDTO>
    >(API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.BASE, {
      params,
      signal: options?.signal,
    });
    return response.data;
  },

  emit: async (
    data: CommercialInvoiceRequestDTO,
  ): Promise<CommercialInvoiceResponseDTO> => {
    const response = await api.post<CommercialInvoiceResponseDTO>(
      API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.BASE,
      data,
      { timeout: SEFAZ_ROUND_TRIP_TIMEOUT_MS },
    );
    return response.data;
  },

  refresh: async (
    id: number,
    options?: RequestOptions,
  ): Promise<CommercialInvoiceResponseDTO> => {
    const response = await api.get<CommercialInvoiceResponseDTO>(
      API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.REFRESH(id),
      { signal: options?.signal, timeout: SEFAZ_ROUND_TRIP_TIMEOUT_MS },
    );
    return response.data;
  },

  cancel: async (
    id: number,
    data: CancelCommercialInvoiceRequestDTO,
  ): Promise<CommercialInvoiceResponseDTO> => {
    const response = await api.delete<CommercialInvoiceResponseDTO>(
      API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.BY_ID(id),
      { data, timeout: SEFAZ_ROUND_TRIP_TIMEOUT_MS },
    );
    return response.data;
  },

  downloadPdf: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(
      API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.PDF(id),
      { responseType: "blob" },
    );
    return response.data;
  },

  downloadXml: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(
      API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.XML(id),
      { responseType: "blob" },
    );
    return response.data;
  },

  downloadCanceledXml: async (id: number): Promise<Blob> => {
    const response = await api.get<Blob>(
      API_ENDPOINTS.COMMERCIAL_INVOICES_ADMIN.XML_CANCELED(id),
      { responseType: "blob" },
    );
    return response.data;
  },
};
